import React, { createContext, useEffect, useState } from "react";
import axios from "axios";
import { DEFAULT_COUNTRY_NAME } from "./Config/officeLocations";

const COUNTRY_CACHE_KEY = "tqVisitorCountry";

// Keep the existing string-based CountryContext API so current forms/pages
// continue to work without changes.
export const CountryContext = createContext(undefined);
export const CountryStatusContext = createContext({
  loading: true,
  error: null,
});

const getCachedCountry = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage.getItem(COUNTRY_CACHE_KEY);
  } catch {
    return null;
  }
};

const cacheCountry = (countryName) => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(COUNTRY_CACHE_KEY, countryName);
  } catch {
    // Location detection must still work when storage is unavailable.
  }
};

export const CountryProvider = ({ children }) => {
  const cachedCountry = getCachedCountry();
  const [country, setCountry] = useState(cachedCountry || undefined);
  const [loading, setLoading] = useState(!cachedCountry);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cachedCountry) return undefined;

    let isMounted = true;

    axios
      .get("https://ipapi.co/json/", { timeout: 5000 })
      .then((response) => {
        if (!isMounted) return;

        const detectedCountry = response?.data?.country_name;
        const resolvedCountry =
          typeof detectedCountry === "string" && detectedCountry.trim()
            ? detectedCountry.trim()
            : DEFAULT_COUNTRY_NAME;

        setCountry(resolvedCountry);
        cacheCountry(resolvedCountry);
        setLoading(false);
      })
      .catch((requestError) => {
        if (!isMounted) return;

        // Requirement: if geolocation cannot be resolved, use the USA office.
        setCountry(DEFAULT_COUNTRY_NAME);
        cacheCountry(DEFAULT_COUNTRY_NAME);
        setError(requestError);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [cachedCountry]);

  return (
    <CountryStatusContext.Provider value={{ loading, error }}>
      <CountryContext.Provider value={country}>
        {children}
      </CountryContext.Provider>
    </CountryStatusContext.Provider>
  );
};
